import React, { useEffect, useState } from 'react';
import 'katex/dist/katex.min.css';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { persistenceService } from './utils/persistenceService';
import DevelopmentGuards from './utils/developmentGuards';
import Signup from './components/Signup';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import MagicLinkHandler from './components/MagicLinkHandler';
import MomentumDashboard from './components/MomentumDashboard';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import OnboardingWizard from './components/OnboardingWizard';
import FirstCheckIn from './components/FirstCheckIn';
import MorningCheckIn from './components/MorningCheckIn';
import EveningCheckIn from './components/EveningCheckIn';
import CheckInPrompt from './components/CheckInPrompt';
import CheckInChat from './components/CheckInChat';
import Explore from './components/Explore';
import Entries from './components/Entries';
import Settings from './components/Settings';
import WelcomeModal from './components/WelcomeModal';
import LandingPage from './components/LandingPage';
import PasswordProtection from './components/PasswordProtection';
import TopHeader from './components/layout/TopHeader';
import BottomNav from './components/layout/BottomNav';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import ToastContainer from './components/ui/ToastContainer';
import ScrollAmbientBackground from './components/ScrollAmbientBackground';
import { ToastProvider } from './contexts/ToastContext';
import { useToast } from './hooks/useToast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './components/ThemeProvider';
import useOnboardingStatus from './hooks/useOnboardingStatus';
import { usePasswordProtection } from './hooks/usePasswordProtection';
import { errorReportingService } from './utils/errorReporting';

function App() {
  const onboardingStatus = useOnboardingStatus();
  const { isAccessGranted, grantAccess } = usePasswordProtection();
  const [showWelcome, setShowWelcome] = useState(false);
  const { toasts, removeToast } = useToast();

  useEffect(() => {
    // Initialize global error reporting
    errorReportingService.initialize();
    
    // Ensure page loads at top on refresh
    window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
    
    const initializeApp = async () => {
      // Run development environment validation first
      await DevelopmentGuards.validateStartup();
      
      try {
        const onboardingData = await persistenceService.getOnboardingData();
        const token = persistenceService.getAuthToken();
        
        // Show welcome modal only if user has started onboarding but hasn't completed it
        if (onboardingData.startedAt && !onboardingData.completedAt && !token) {
          setShowWelcome(true);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    };

    initializeApp();
  }, []);

  // Enforce global password protection
  if (isAccessGranted === false) {
    return (
      <ErrorBoundary>
        <ThemeProvider>
          <PasswordProtection onAccessGranted={grantAccess} />
        </ThemeProvider>
      </ErrorBoundary>
    );
  }

  if (isAccessGranted === null) {
    return (
      <ScrollAmbientBackground className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-6 animate-bounce">
            <circle cx="32" cy="32" r="24" fill="#f43f5e" fillOpacity="0.8" />
            <circle cx="32" cy="24" r="12" fill="#f43f5e" />
          </svg>
          <div className="text-xl font-bold text-text-primary font-inter">Loading ThinkMate...</div>
        </div>
      </ScrollAmbientBackground>
    );
  }

  if (onboardingStatus === 'loading') {
    return (
      <ScrollAmbientBackground className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="mb-6 animate-bounce">
            <circle cx="32" cy="32" r="24" fill="#f43f5e" fillOpacity="0.8" />
            <circle cx="32" cy="24" r="12" fill="#f43f5e" />
          </svg>
          <div className="text-xl font-bold text-text-primary font-inter">Loading ThinkMate...</div>
        </div>
      </ScrollAmbientBackground>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <Router future={{ 
              v7_startTransition: true,
              v7_relativeSplatPath: true 
            }}>
              {showWelcome && <WelcomeModal />}
              <AppWithTabs onboardingStatus={onboardingStatus} />
              <ToastContainer toasts={toasts} onClose={removeToast} />
            </Router>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

// Helper to check for specific user override
const isKaizenwerksUser = (): boolean => {
  const profile = persistenceService.getUserProfile();
  const userEmail = localStorage.getItem('userEmail');
  return profile?.email === 'kaizenwerks@gmail.com' || userEmail === 'kaizenwerks@gmail.com';
};

// Helper to use location inside Router
const AppWithTabs: React.FC<{ onboardingStatus: string }> = ({ onboardingStatus }) => {
  const location = useLocation();
  
  // If onboarding, render only onboarding flow (full-page takeover)
  if (location.pathname.startsWith('/onboarding')) {
    return (
      <ScrollAmbientBackground className="min-h-screen flex flex-col">
        <Routes>
          <Route 
            path="/onboarding" 
            element={
              <ProtectedRoute>
                <OnboardingWizard />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </ScrollAmbientBackground>
    );
  }
  
  // If landing page, render without header/nav (full-page takeover)
  if (location.pathname === '/' || location.pathname === '/landing') {
    return (
      <ScrollAmbientBackground className="min-h-screen">
        <Routes>
          <Route path="/landing" element={<LandingPage />} />
          <Route
            path="/"
            element={
              localStorage.getItem('token') ? (
                (onboardingStatus !== 'complete' && !isKaizenwerksUser()) ? (
                  <Navigate to="/onboarding" replace />
                ) : (
                <Navigate to="/dashboard" replace />
                )
              ) : (
                <LandingPage />
              )
            }
          />
        </Routes>
      </ScrollAmbientBackground>
    );
  }
  
  return (
    <ScrollAmbientBackground className="min-h-screen flex flex-col">
      <TopHeader />
      <main className="flex-1 w-full max-w-3xl mx-auto pb-20 md:pb-0 px-0 md:px-8 transition-colors duration-300">
        <Routes>
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/magic" element={<MagicLinkHandler />} />
          <Route 
            path="/onboarding" 
            element={
              <ProtectedRoute>
                <OnboardingWizard />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/check-in/day-1"
            element={
              <ProtectedRoute>
                <FirstCheckIn />
              </ProtectedRoute>
            }
          />
          <Route
            path="/check-in/morning"
            element={
              <ProtectedRoute>
                <MorningCheckIn />
              </ProtectedRoute>
            }
          />
          <Route
            path="/check-in/evening"
            element={
              <ProtectedRoute>
                <EveningCheckIn />
              </ProtectedRoute>
            }
          />
          <Route
            path="/check-in"
            element={
              <ProtectedRoute>
                <CheckInPrompt />
              </ProtectedRoute>
            }
          />
          <Route
            path="/check-in-chat"
            element={
              <ProtectedRoute>
                <CheckInChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                {(onboardingStatus !== 'complete' && !isKaizenwerksUser()) ? <Navigate to="/onboarding" replace /> : <Dashboard />}
              </ProtectedRoute>
            }
          />
          <Route
            path="/momentum-dashboard"
            element={
              <ProtectedRoute>
                <MomentumDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/explore" element={<ProtectedRoute><Explore /></ProtectedRoute>} />
          <Route path="/entries" element={<ProtectedRoute><Entries /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        </Routes>
      </main>
      {!(location.pathname === '/signup' || location.pathname === '/login' || location.pathname === '/magic') && <BottomNav />}
    </ScrollAmbientBackground>
  );
};

export default App;
